/* eslint-disable max-len */
import React, { useEffect } from 'react';
import proptypes from 'prop-types';
import { useBreakpoint, Typography } from '@one-thd/sui-atomic-components';
import { ComponentSelector } from '@thd-olt-component-react/component-selector';
import { SponsoredUniversalBanner } from '@thd-olt-component-react/sponsored-content';
import classnames from 'classnames';
import { BentoBay } from './sub-components/BentoBay';
import { BentoHeroCarousel } from './sub-components/BentoHeroCarousel';
import { ADOBE_TARGET, COMPONENT_SELECTOR } from './constants';
import './bento-hero-container.scss';

const { arrayOf, bool, shape } = proptypes;
const FIRST_BAY_POSITION = 1;
const SECOND_BAY_POSITION = 2;
const THIRD_BAY_POSITION = 3;
const FOURTH_BAY_POSITION = 4;

// Responsible for rendering the grid and which cells of the grid to show visually at each breakpoint.
// Breakdown (4 bay layout):
//   Mobile/Tablet breakpoint --
//     - 1st cell has 100% width (full width).
//     - 2nd cell has 50% width.
//     - 3rd cell has 50% width.
//     - 4th cell has 100% width.
//   Desktop breakpoint --
//     - 1st cell has ~66% width.
//     - 2nd cell has ~33% width.
//     - 3rd cell has 50% width.
//     - 4th cell has 50% width.
const BentoHeroContainer = ({
  data, enableB2CRadius = false, isSeoBot = false, renderDefault = false, variationIds = []
}) => {
  // A breakpoint cannot be both mobile and not mobile at the same time, and if they are the same (most likely false)
  // it means we have not fully loaded on the client yet. We want to wait until we have fully loaded on the client
  // otherwise we could be creating the wrong impression ID, clickID, (the ID will contain the wrong position for the
  // Bay in a 5 bay layout).
  const { lesser: isMobile, exact, greater } = useBreakpoint('lg', { ssr: true, current: true });

  // Wait until the breakpoint is actually resolved before setting positions.
  // When ssr: true, all values (lesser, greater, exact) start as false.
  // At least one must be true once the viewport has been measured.
  const impressionsAreReady = isMobile || greater || exact;
  const thirdDynamicBayPosition = isMobile ? THIRD_BAY_POSITION : FOURTH_BAY_POSITION;
  const fourthDynamicBayPosition = isMobile ? FOURTH_BAY_POSITION : THIRD_BAY_POSITION;

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bentoHero.ready');
  }, []);

  if (!data) {
    return null;
  }

  const {
    firstBay, secondBay, thirdBay, fourthBay, bentoSponsoredBanner, isLivePreviewData
  } = data;

  if (!firstBay && !bentoSponsoredBanner && !isLivePreviewData) return null;

  const { schema = null, specialDefaultBanner = {} } = bentoSponsoredBanner || {};

  let contentfulDefaultBanner = null;
  if (specialDefaultBanner?.imgDesktop && specialDefaultBanner?.imgMobile) {
    contentfulDefaultBanner = { specialDefaultBanner };
  }

  const processSecondBayComponentSelector = () => {
    const secondBayComponent = secondBay?.components?.[0];
    const isSecondBayComponentSelector = secondBayComponent?.__typename === COMPONENT_SELECTOR;
    const secondBayComponentSelectorTargetingType = secondBayComponent?.targetingType?.__typename;
    let secondBayData = secondBayComponent?.defaultComponent?.content;

    if (isSecondBayComponentSelector && !renderDefault && secondBayComponentSelectorTargetingType === ADOBE_TARGET) {
      const targetId = secondBayComponent?.targetingType?.mbox;
      const matchedVariation = variationIds?.find((variation) => variation.uniqueID === targetId);
      if (matchedVariation) {
        const variationIdsSlotNumber = matchedVariation.id;
        const matchedSlot = secondBayComponent?.variations.find((slot) => variationIdsSlotNumber === slot.slotNumber);
        if (matchedSlot) {
          secondBayData = matchedSlot.content;
        }
      }
    }

    return { isSecondBayComponentSelector, secondBayComponent, secondBayData };
  };
  const { isSecondBayComponentSelector, secondBayComponent, secondBayData } = processSecondBayComponentSelector();

  const fourBentoBayLayout = (
    <div className="sui-grid sui-grid-cols-12 sui-gap-4 md:sui-gap-6 xl:sui-gap-8 sui-bg-primary">
      <div className="sui-col-span-12 lg:sui-col-span-8 sui-aspect-[1.43/1] md:sui-aspect-[3/1] lg:sui-aspect-[3.33/1] sui-h-full sui-w-full">
        {firstBay && (
          <BentoBay
            bentoBayData={firstBay}
            position={FIRST_BAY_POSITION}
            enableB2CRadius={enableB2CRadius}
          />
        )}
      </div>
      <div className="sui-col-span-6 lg:sui-col-span-4 sui-aspect-[1/1.47] md:sui-aspect-[1.45/1] lg:sui-aspect-[1.6/1] sui-h-full sui-w-full">
        {isLivePreviewData ? (
          <div className="sui-flex sui-items-center sui-bg-strong sui-justify-center sui-w-full sui-py-4">
            <Typography>SponsoredBanner Placeholder</Typography>
          </div>
        ) : (
          <>
            {bentoSponsoredBanner && (
              <SponsoredUniversalBanner
                bentoBayData={bentoSponsoredBanner}
                contentfulDefaultBanner={contentfulDefaultBanner}
                impressionReady={impressionsAreReady}
                position={SECOND_BAY_POSITION}
                schema={schema}
                renderDefaultBanner
                bannerType="standard"

              />
            )}
          </>
        )}
      </div>
      <div className="sui-col-span-6 sui-aspect-[1/1.47] md:sui-aspect-[1.45/1] lg:sui-aspect-[4.93/1] sui-h-full sui-w-full">
        {secondBay && (
          <BentoBay
            bentoBayData={secondBay}
            position={THIRD_BAY_POSITION}
            enableB2CRadius={enableB2CRadius}
          />
        )}
      </div>
      <div className="sui-col-span-12 lg:sui-col-span-6 sui-aspect-[2.86/1] md:sui-aspect-[6/1] lg:sui-aspect-[4.93/1] sui-h-full sui-w-full">
        {thirdBay && (
          <BentoBay
            bentoBayData={thirdBay}
            position={FOURTH_BAY_POSITION}
            enableB2CRadius={enableB2CRadius}
          />
        )}
      </div>
    </div>
  );

  const fiveBentoBayLayout = (
    <div className="sui-grid sui-grid-flow-row sui-grid-cols-12 sui-gap-4 md:sui-gap-6 xl:sui-gap-8 sui-bg-primary">
      {firstBay && firstBay?.isCarousel ? (
        <BentoHeroCarousel
          carouselData={firstBay?.components}
          position={FIRST_BAY_POSITION}
          isAutoplay={firstBay?.isAutoplay}
          enableB2CRadius={enableB2CRadius}
        />
      ) : (
        <div className={classnames('sui-col-span-12 lg:sui-col-span-8 sui-order-1 lg:sui-order-2 sui-w-full', {
          'bento-clip-path-rounded': enableB2CRadius,
        })}
        >
          {firstBay && (
            <BentoBay
              bentoBayData={firstBay}
              position={FIRST_BAY_POSITION}
              isSponsored
              enableB2CRadius={enableB2CRadius}
            />
          )}
        </div>
      )}
      {/* Sponsored Content */}
      <div
        className={classnames(
          'bento-hero-sponsored-content-wrapper',
          'sui-col-span-12 lg:sui-col-span-12 sui-order-2 lg:sui-order-3 lg:sui-cols-4 sui-w-full',
          {
            'bento-clip-path-rounded': enableB2CRadius,
          }
        )}
      >
        {isLivePreviewData ? (
          <div className="sui-flex sui-items-center sui-bg-strong sui-justify-center sui-w-full sui-py-4">
            <Typography>SponsoredBanner Placeholder</Typography>
          </div>
        ) : (
          <>
            {bentoSponsoredBanner && (
              <SponsoredUniversalBanner
                bentoBayData={bentoSponsoredBanner}
                contentfulDefaultBanner={contentfulDefaultBanner}
                data-testid="sponsored-banner"
                impressionReady={impressionsAreReady}
                position={FIRST_BAY_POSITION}
                schema={schema}
                renderDefaultBanner
                bannerType="standard"
              />
            )}
          </>
        )}
      </div>
      <div className={classnames('sui-col-span-12 lg:sui-col-span-4 sui-order-3 lg:sui-order-2 lg:sui-aspect-[1.8/1] sui-w-full sui-h-full', {
        'bento-clip-path-rounded': enableB2CRadius,
      })}
      >
        {secondBay && (
          isSecondBayComponentSelector ? (
            <ComponentSelector
              componentMap={{ HeroFlattenImage: BentoBay }}
              key={secondBayComponent.id}
              componentId={secondBayComponent.id}
              componentClass={COMPONENT_SELECTOR}
              customPropMap={{
                HeroFlattenImage: {
                  bentoBayData: secondBayData,
                  position: SECOND_BAY_POSITION,
                  enableB2CRadius
                }
              }}
              isInCarousel={false}
              defaultComponent={secondBayComponent.defaultComponent}
              variations={secondBayComponent.variations}
              variationIds={variationIds}
              renderDefault={renderDefault}
              isSeoBot={isSeoBot}
              targetingType={secondBayComponent.targetingType}
            />
          ) : (
            <BentoBay
              bentoBayData={secondBay}
              position={SECOND_BAY_POSITION}
              enableB2CRadius={enableB2CRadius}
            />
          )
        )}
      </div>
      <div className={classnames('sui-col-span-12 lg:sui-col-span-8 sui-order-4 lg:sui-order-5 lg:sui-aspect-[3.6/1] sui-w-full sui-h-full', {
        'bento-clip-path-rounded': enableB2CRadius,
      })}
      >
        {thirdBay && (

          <BentoBay
            key={`thirdBay-${impressionsAreReady}`}
            bentoBayData={thirdBay}
            position={thirdDynamicBayPosition}
            enableB2CRadius={enableB2CRadius}
          />
        )}
      </div>
      <div className={classnames('sui-col-span-12 lg:sui-col-span-4 sui-order-5 lg:sui-order-4 lg:sui-aspect-[1.8/1] sui-w-full sui-h-full', {
        'bento-clip-path-rounded': enableB2CRadius,
      })}
      >
        {fourthBay && (

          <BentoBay
            key={`fourthBay-${impressionsAreReady}`}
            bentoBayData={fourthBay}
            position={fourthDynamicBayPosition}
            enableB2CRadius={enableB2CRadius}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      {fourthBay ? fiveBentoBayLayout : fourBentoBayLayout}
    </>
  );
};

BentoHeroContainer.propTypes = {
  data: shape({
    firstBay: shape({}),
    secondBay: shape({}),
    thirdBay: shape({}),
    fourthBay: shape({}),
    bentoSponsoredBanner: shape({}),
  }).isRequired,
  enableB2CRadius: bool,
  isSeoBot: bool,
  renderDefault: bool,
  variationIds: arrayOf(shape({}))
};

export { BentoHeroContainer };
